import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { SKIP_GBE_ERROR_NOTIFICATION } from '@core/interceptors/gbe-error.interceptor';

import { GbeEndPointV4 } from '../../models/gbe/gbe-endpoint.enum';
import {
  CustomTransformerResponse,
  DataTransformerRequest,
  DataTransformerSuccessResponse,
  GetTransformersParams,
  Transformers
} from '../../models/transformers.model';
import { GBE_BASE_URL } from '../../tokens/gbe-url.token';

@Injectable({
  providedIn: 'root'
})
export class CsvTransformerService {
  private readonly http = inject(HttpClient);
  private readonly gbeUrl = inject(GBE_BASE_URL);

  private buildUrl(endpoint: string): string {
    return `${this.gbeUrl}/${endpoint}`;
  }

  /**
   * Retrieves the list of available transformers
   *
   * @param params Optional parameters to filter (name, version)
   * @returns Observable<Transformers>
   */
  public getTransformers(params?: GetTransformersParams): Observable<Transformers> {
    const url = this.buildUrl(GbeEndPointV4.TRANSFORMERS);

    let httpParams = new HttpParams();
    if (params?.name) {
      httpParams = httpParams.set('name', params.name);
    }
    if (params?.version) {
      httpParams = httpParams.set('version', params.version.toString());
    }
    if (params?.dataSpaceId !== undefined) {
      httpParams = httpParams.set('data_space_id', params.dataSpaceId.toString());
    }
    return this.http.get<Transformers>(url, {
      params: httpParams.toString() ? httpParams : undefined
    });
  }

  /**
   * Transforms a CSV using a specific transformer
   *
   * @param transformerData The transformer data and the CSV to process
   * @param dataSpaceId Optional ID of the data space
   * @param options `skipErrorNotification` for a caller that renders the failure itself
   * @returns Observable<DataTransformerSuccessResponse>
   */
  public postTransformers<T = unknown>(
    transformerData: DataTransformerRequest<T>,
    dataSpaceId?: number,
    options?: { skipErrorNotification?: boolean }
  ): Observable<DataTransformerSuccessResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.TRANSFORMERS}`);

    // Prepare HTTP parameters
    let httpParams = new HttpParams();
    if (dataSpaceId) {
      httpParams = httpParams.set('data_space_id', dataSpaceId);
    }

    return this.http.post<DataTransformerSuccessResponse>(url, transformerData, {
      params: httpParams.toString() ? httpParams : undefined,
      context: options?.skipErrorNotification
        ? new HttpContext().set(SKIP_GBE_ERROR_NOTIFICATION, true)
        : undefined
    });
  }

  public getCustomTransformers(
    dataSpaceId: number,
    entityType: string
  ): Observable<CustomTransformerResponse[]> {
    const url = this.buildUrl(`${GbeEndPointV4.TRANSFORMERS}/custom`);

    // Prepare HTTP parameters
    let httpParams = new HttpParams();
    httpParams = httpParams.set('data_space_id', dataSpaceId);
    httpParams = httpParams.set('entity_type', entityType.toUpperCase());

    return this.http.get<CustomTransformerResponse[]>(url, {
      params: httpParams.toString() ? httpParams : undefined
    });
  }
}
